// src/features/projects/briefing/landingModel.js
//
// Pure helpers for the Projects Portfolio landing (task 8674): type pills,
// seven-phase dots, canonical current-week posting status, briefing peek
// track selection and Projects alert-chip messages. No React, no fetches.

import { T, PROJECT_TYPE } from "../../../app/tokens.js";
import { getProjectPhaseSteps, mapProjectPhaseToDisplayPhase, getProjectPhaseDisplayLabel } from "../projectFilters.js";
import { TRACKS, resolveLine } from "../weekly/weeklyModel.js";
import { countReportedTracks, lineInWeek } from "../weekly/ProjectWorkingUpdatesView.jsx";

export { countReportedTracks };

const TRACK_ORDER = new Map(TRACKS.map((t, i) => [t.key, i]));
const isBlank = (s) => typeof s !== "string" || !s.trim();

// ── Type pill ────────────────────────────────────────────────────────────────
// New Build blue, Renovation slate, Exit red; other types keep their existing
// honest labels and legacy palette.
export function typePillMeta(type) {
  if (type === "new_build") return { label: "New Build", fg: T.projectsTypeBuildFg, bg: T.projectsTypeBuildBg };
  if (type === "renovation") return { label: "Renovation", fg: T.projectsTypeRenoFg, bg: T.projectsTypeRenoBg };
  if (type === "exit") return { label: "Exit", fg: T.projectsTypeExitFg, bg: T.projectsTypeExitBg };
  const info = PROJECT_TYPE[type];
  return info ? { label: info.label, fg: info.color, bg: info.bg } : { label: type || "Not set", fg: T.mid, bg: T.lightGray };
}

// ── Phase dots ───────────────────────────────────────────────────────────────
// Reuses the full record's seven-phase/legacy-alias mapping. Unknown phases
// return all-future dots and known=false (no fabricated progress).
export function phaseDots(phase) {
  const display = mapProjectPhaseToDisplayPhase(phase);
  return {
    known: display != null,
    complete: display === "complete",
    label: display == null ? (phase ? `Recorded: ${phase}` : "Phase not set") : getProjectPhaseDisplayLabel(phase),
    steps: getProjectPhaseSteps(phase).map((s) => ({ ...s, state: display == null ? "upcoming" : s.state })),
  };
}

// ── Canonical current-week posting status ────────────────────────────────────
// Display rule (evidence-based, documented in POSTING_RULE_HELP):
//   loading       - current-week overview read pending
//   error         - read failed (never shown as Not yet)
//   absent        - project not in the canonical weekly population
//   posted        - at least one contributor marked their update complete AND
//                   at least one canonical track has an actual line
//   partial       - actual track lines without any completion, or a completion
//                   acknowledgement with no actual track lines
//   not_yet       - in the population, no actual lines and no completion
//                   (status-only entries stay not_yet, flagged statusOnly)
// "Posted" does not claim that every assigned contributor or all four
// tracks have reported; the payload has no expected-contributor count.
export const POSTING_RULE_HELP =
  "Posted: someone marked their update complete for this week and at least one track has an update line. "
  + "Partial: track lines without a completion, or a completion with no track lines. "
  + "Not yet: no track lines and no completion (track status alone does not count). "
  + "Posted does not mean every contributor or all four tracks have reported.";

export const POSTING_META = {
  posted:  { label: "Posted",  dot: T.projectsPostedDot },
  partial: { label: "Partial", dot: T.projectsPartialDot },
  not_yet: { label: "Not yet", dot: T.projectsNotYetDot },
  absent:  { label: "Not in week", dot: null },
  loading: { label: "Loading", dot: null },
  error:   { label: "Unavailable", dot: null },
};

export function findOverviewProject(data, projectId) {
  return (data?.projects || []).find((p) => String(p.id) === String(projectId)) || null;
}

export function postingStatus(weekState, projectId) {
  if (!weekState || weekState.loading) return { key: "loading", reported: null };
  if (weekState.error || !weekState.data) return { key: "error", reported: null };
  const project = findOverviewProject(weekState.data, projectId);
  if (!project) return { key: "absent", reported: null };
  const weekDate = weekState.data.weekDate;
  const reported = countReportedTracks(project, weekDate);
  const completions = (project.completions || []).filter((c) => !c.weekDate || String(c.weekDate).slice(0, 10) === weekDate).length;
  const statusOnly = reported === 0 && (project.statuses || []).length > 0;
  let key = "not_yet";
  if (reported > 0 && completions > 0) key = "posted";
  else if (reported > 0 || completions > 0) key = "partial";
  return { key, reported, completions, statusOnly };
}

// ── Briefing peek track updates ──────────────────────────────────────────────
// Deterministic: canonical track order, then createdAt, then id. At most
// `limit` actual lines (nonblank original or current, including struck).
export function peekTrackUpdates(project, weekDate, limit = 2) {
  return (project?.lines || [])
    .filter((l) => TRACK_ORDER.has(l.trackKey) && lineInWeek(l, weekDate))
    .filter((l) => { const r = resolveLine(l); return !isBlank(r.original) || !isBlank(r.current); })
    .sort((a, b) => (TRACK_ORDER.get(a.trackKey) - TRACK_ORDER.get(b.trackKey))
      || String(a.createdAt || "").localeCompare(String(b.createdAt || ""))
      || String(a.id).localeCompare(String(b.id)))
    .slice(0, limit);
}

// ── Decisions ────────────────────────────────────────────────────────────────
function addDaysIso(iso, days) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso || "");
  if (!m) return null;
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]) + days));
  return d.toISOString().slice(0, 10);
}

/** Open decisions with a needed-by date Monday..Sunday of `weekDate`. */
export function decisionsDueInWeek(decisions, weekDate) {
  const end = addDaysIso(weekDate, 6);
  if (!end) return [];
  return (decisions || []).filter((d) => {
    const due = String(d?.neededByDate || d?.needed_by_date || "").slice(0, 10);
    return (d?.status == null || d.status === "open") && due >= weekDate && due <= end;
  });
}

export function nextOpenDecision(decisions) {
  const open = (decisions || []).filter((d) => d?.status == null || d.status === "open");
  return [...open].sort((a, b) => {
    const ad = String(a.neededByDate || ""); const bd = String(b.neededByDate || "");
    if (!ad !== !bd) return ad ? -1 : 1;
    return ad.localeCompare(bd) || String(a.id).localeCompare(String(b.id));
  })[0] || null;
}

// ── Alert chip messages ──────────────────────────────────────────────────────
// Population: active (non-complete) projects in the current view. Returns null
// while inputs are unresolved so the caller can hold prior messages instead of
// presenting a false all-clear.
export function buildProjectAlertMessages({ projects, projectsReady, hub, onShowAtRisk, onOpenWeekly, onRetryWeekly }) {
  if (!projectsReady) return null;
  const active = (projects || []).filter((p) => p && p.phase !== "complete");
  const critical = active.filter((p) => p.statusOverall === "critical").length;
  const atRisk = active.filter((p) => p.statusOverall === "at_risk").length;
  const messages = [];
  if (critical + atRisk > 0) {
    const parts = [];
    if (critical) parts.push(`${critical} critical`);
    if (atRisk) parts.push(`${atRisk} at risk`);
    messages.push({
      id: "projects-health",
      severity: critical ? "urgent" : "warn",
      text: `${critical + atRisk} active project${critical + atRisk === 1 ? "" : "s"} in view need attention - ${parts.join(", ")}`,
      actionLabel: "Show projects",
      onAction: onShowAtRisk,
    });
  }
  // Every declared read must settle before messages change; pending returns
  // null so the caller holds prior complete messages (no false all-clear).
  if (!hub || hub.loading) return null;
  if (hub?.error) {
    messages.push({ id: "projects-decisions-unavailable", severity: "warn", text: "Decisions due this week could not be loaded", actionLabel: "Retry", onAction: onRetryWeekly });
  } else if (Number.isFinite(hub?.data?.decisionsDue) && hub.data.decisionsDue > 0) {
    const n = hub.data.decisionsDue;
    messages.push({ id: "projects-decisions-due", severity: "warn", text: `${n} open decision${n === 1 ? "" : "s"} due this week on active projects in view`, actionLabel: "Open Weekly Review", onAction: onOpenWeekly });
  }
  return messages;
}
